import { EMPTY_STRING_INDICATOR } from "src/app/constants";

import type { SearchConfig } from "../types";

/**
 * The reviewed source of `product-liability-search-config.json` in each stage's
 * configs bucket, and still read directly by the legacy International Product
 * Liability component for its search fields. Change it and the stored documents
 * together. See `SearchConfigRegistryEntry.searchConfig`.
 */
export const IPL_SEARCH_CONFIG: SearchConfig = {
  lob: "ipl",
  claimType: "product-liability",
  retrievalPageSize: 100,
  testId: "ipl-claims-search-results",

  searchableFields: [
    {
      fieldId: "orderId",
      // Matches `product-liability-query-config.json` as deployed to alpha, beta
      // and gamma. Prod is unverified.
      queryName: "byOrderId",
      queryInputProperty: "orderId",
      maxTermLength: 25,
      labels: {
        buttonText: "Search",
        buttonTextSmall: "Search",
        placeholder: "Search for IPL claims using the Order ID",
        placeholderSmall: "Search by Order ID",
      },
      metrics: [
        {
          publisherName: "iplSearchByOrderId",
          counterName: { kind: "literal", value: "iplClaimsSearchByOrderId" },
        },
      ],
    },
  ],

  table: {
    columns: [
      {
        columnId: "claimNumber",
        header: "Claim Number",
        source: "claimNumber",
        sorting: { kind: "field", sortingField: "claimNumber" },
        emptyPlaceholder: EMPTY_STRING_INDICATOR,
      },
      {
        columnId: "orderId",
        header: "Order ID",
        source: "orderId",
        sorting: { kind: "field", sortingField: "orderId" },
        emptyPlaceholder: EMPTY_STRING_INDICATOR,
      },
      {
        columnId: "claimReporterAlias",
        header: "Claim Submitter",
        source: "claimReporterAlias",
        sorting: { kind: "field", sortingField: "claimReporterAlias" },
        emptyPlaceholder: EMPTY_STRING_INDICATOR,
      },
      {
        columnId: "itemPurchasedMarketplace",
        header: "Marketplace",
        source: "itemPurchasedMarketplace",
        sorting: { kind: "field", sortingField: "itemPurchasedMarketplace" },
        emptyPlaceholder: EMPTY_STRING_INDICATOR,
      },
      {
        columnId: "incidentDate",
        header: "Incident Date",
        source: "incidentDate",
        sorting: { kind: "field", sortingField: "incidentDate" },
        emptyPlaceholder: EMPTY_STRING_INDICATOR,
      },
      {
        columnId: "createdAtTimestamp",
        header: "Submitted Date",
        source: "createdAtTimestamp",
        sorting: { kind: "field", sortingField: "createdAtTimestamp" },
        derivation: { kind: "epochSecondsToDate" },
        emptyPlaceholder: EMPTY_STRING_INDICATOR,
      },
    ],

    defaultSorting: { columnId: "createdAtTimestamp", direction: "descending" },

    pageSize: 10,
    minItemsForControls: 5,
    loadingText: "Loading IPL Claims",
    resourceName: "IPL Claim",
    resultsHeaderText: "IPL Claims Search Results",
    filterPlaceholder: "Filter IPL claims",
    filterAriaLabel: "Filter IPL claims",
    moreResultsSuffix: " (More claims available)",
  },

  exportConfig: {
    fileNamePrefix: "ipl_claims_search_results",
    fields: [
      { label: "Claim Number", source: "claimNumber" },
      { label: "Order ID", source: "orderId" },
      { label: "Claim Submitter", source: "claimReporterAlias" },
      { label: "Marketplace", source: "itemPurchasedMarketplace" },
      { label: "Incident Date", source: "incidentDate" },
      {
        label: "Submitted Date",
        source: "createdAtTimestamp",
        derivation: { kind: "epochSecondsToDate" },
      },
    ],
  },

  metrics: {
    configRetrievalPublisherName: "claimsHistory.resolveSearchConfig",
    pageVisited: [
      {
        publisherName: "iplSearchResultsVisited",
        counterName: { kind: "literal", value: "iplSearchResultsVisited" },
      },
    ],
    searchSubmitted: [
      {
        publisherName: "iplSearch",
        counterName: { kind: "literal", value: "iplClaimsSearchBarUsed" },
      },
    ],
  },
};
